import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { DropdownSelectorBlock } from 'App/Map/PanelDetails/SectionTitleBars/DropdownSelectorBlock/DropdownSelectorBlock'
import { SprayConditionsInfo } from 'App/Map/PanelDetails/SectionTitleBars/SprayConditionsTitleChildren/SprayConditionsInfo'
import { getTimezoneForSelectedPropertyOrRegion } from 'App/Map/PanelDetails/_utils/getTimezoneForSelectedPropertyOrRegion'
import { propertyLacksPermissionSectionMaker } from 'App/Map/PanelDetails/_utils/propertyLacksPermissionSectionMaker'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import type { TValuesTimeseriesBlocks } from 'App/Map/types'
import type { StackedChartSection } from 'components/StackedChart/types'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import type { TSelectedFieldAssetsStoreState } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import type { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { doesSelectedBlockHaveValueTypes } from 'utils/doesSelectedFieldAssetHaveValueTypes'
import { chooseAmongstUnAggHourlyAndDaily } from '../../chooseAmongstUnAggHourlyAndDaily'
import { Legend } from './Legend/Legend'
import { makeIntervals } from './makeIntervals'

const checkPermission = () => selectedPropertyHasPermission({ permission: 'VIEW_SPRAY_FORECAST' })
const valueType: TValuesTimeseriesBlocks = 'sprayConditions'

export const apiArgs = ({
  selectedValueGroups,
  selectedFieldAssets,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}): Partial<routes.Values.Request> => {
  if (!checkPermission()) return {}

  if (!selectedValueGroups.spray_conditions || !selectedFieldAssets.block) return {}

  const aggInterval = chooseAmongstUnAggHourlyAndDaily()

  const preferredAggregationInterval = {
    preferredAggregationInterval: aggInterval === 'UNAGGREGATED' ? 'HOURLY' : aggInterval,
  }

  if (
    !doesSelectedBlockHaveValueTypes({
      valuesTimeseries: [valueType],
    })
  )
    return {}

  return {
    blocks: {
      blockIds: [selectedFieldAssets.block],
      valuesRequested: {
        [valueType]: preferredAggregationInterval,
      },
    },
  }
}

export const content = ({ data }: { data: routes.Values.Response }): StackedChartSection => {
  const commonReturnItems = {
    title: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{translate.phrases.banyanApp('Spray Conditions')}</span>
        <SprayConditionsInfo valuesTimeseriesToFilterOn={['sprayConditions']} />
      </div>
    ),
    fieldAssetPicker: <DropdownSelectorBlock valuesTimeseriesToFilterOn={['sprayConditions']} />,
    id: 'stackem-spray-conditions',
  }

  if (!checkPermission()) return propertyLacksPermissionSectionMaker(commonReturnItems)

  const timezone = getTimezoneForSelectedPropertyOrRegion()
  const blockId = String(selectedFieldAssetsStore.getState().block)
  const { metadata, timeseries } = data?.blocks?.[blockId]?.values?.sprayConditions?.[0] ?? {}
  const isDaily = metadata?.aggregationInterval === 'DAILY'
  const intervals = makeIntervals({ interval: isDaily ? 'day' : 'hour', timeseries, timezone })

  return {
    ...commonReturnItems,
    items: [
      {
        childrenLower: <Legend intervals={intervals} />,
        chartConfig: {
          semiosHighchartsAdditions: {
            id: 'Spray Conditions',
            firstForecastTimestamp: +new Date(),
          },
          chart: {
            type: 'xrange',
            height: 200,
          },
          tooltip: {
            xDateFormat: isDaily ? '%D' : '%Z',
            formatter: function () {
              const { color, name, x, x2 } = this.points?.[0]?.point ?? {}
              const dateFrom = moment.tz(x, timezone)
              const dateTo = moment.tz(x2, timezone)
              const formatToUse = isDaily ? 'MMM D, YYYY' : 'ddd, MMM D, YYYY h:mm a (z)'
              const formattedDateFrom = translate.dates.format(dateFrom, formatToUse)
              const formattedDateTo = translate.dates.format(dateTo, formatToUse)

              return `
              <div style="font-size:16px;display:flex;flex-direction:column;gap:4px 12px">
                <div style="display:flex;align-items:center;gap:6px">
                  <div style="width:12px;height:12px;background:${color}"></div>
                  <div style="font-weight:bold">${name}</div>
                </div>
                <table>
                  <tr>
                    <td style="font-weight:bold;padding:0 6px">${translate.phrases.banyanApp('From')}</td>
                    <td>${formattedDateFrom}</td>
                  </tr>
                  <tr>
                    <td style="font-weight:bold;padding:0 6px">${translate.phrases.banyanApp('To')}</td>
                    <td>${formattedDateTo}</td>
                  </tr>
                </table>
              </div>`
            },
          },
          series: [
            {
              showInLegend: false, // we make a manual legend instead in childrenLower
              borderRadius: 0,
              minPointLength: 3,
              borderWidth: 0,
              enableMouseTracking: true,
              type: 'xrange',
              id: 'sprayConditions',
              name: translate.phrases.banyanApp('Spray Conditions'),
              data: intervals,
            },
          ],
        },
      },
    ],
    title: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{translate.phrases.banyanApp('Spray Conditions')}</span>
        <SprayConditionsInfo
          sprayConditionsRules={metadata?.sprayConditionsRules}
          valuesTimeseriesToFilterOn={['sprayConditions']}
        />
      </div>
    ),
  }
}
